/**
 * Cart Drawer Bonus Threshold Bar
 * Displays a progress bar in the slide-out cart drawer showing how close
 * the user is to unlocking free bonus entries at dollar milestones.
 *
 * Data sources:
 *   - Tiers: Clean Sweeps spend-bonus rules for the sweepstake in the
 *     `cs_sweepstake_id` theme setting (passed as data-sweepstake-id), via
 *     window.CS_BONUS_TIERS from cart-entries.js. Same rules the cart page's
 *     Clean Sweeps bonus block shows. "default" = first active sweepstake.
 *   - Cart subtotal: /cart.js, measured the way Clean Sweeps awards the bonus
 *     (original price unless the sweepstake calculates with discounts).
 * Updates on: drawer open, cart:update / cart:updated / cart:item-added,
 * cs:cart-entries-updated, DOMContentLoaded.
 *
 * Layout: markers are evenly spaced regardless of dollar value, and the fill
 * interpolates between them, so 2-5 tiers keep the same shape. 4+ tiers use
 * a compact size; more than MAX_TRACK_TIERS shows only the status line. With
 * no tier data the bar stays hidden rather than show stale numbers.
 *
 * The bar container lives inside cart-items-component and gets replaced on
 * section refresh. This script lives OUTSIDE that component, so it
 * persists across refreshes. On each update it checks whether the inner
 * content needs to be rebuilt.
 */
(() => {
  'use strict';

  /* ── Configuration ─────────────────────────────────────────────────── */
  const BAR_ID = 'drawer-bonus-bar';
  const DEBOUNCE_MS = 300;
  const COMPACT_TIER_COUNT = 4;
  const MAX_TRACK_TIERS = 5;

  /* ── State ─────────────────────────────────────────────────────────── */
  let debounceTimer = null;
  let listenersAttached = false;
  let requestSequence = 0;
  let contentObserver = null;
  // Last rendered state, replayed when a section morph empties the content.
  let lastRender = null;

  /* ── Helpers ────────────────────────────────────────────────────────── */
  const withCommas = (value) => value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  const formatDollars = (amount) => {
    const rounded = Math.round(amount * 100) / 100;
    return Number.isInteger(rounded) ? `$${withCommas(rounded)}` : `$${rounded.toFixed(2)}`;
  };

  // Evenly spaced: tier i of n sits at (i + 1) / n of the track.
  const tierPosition = (index, tierCount) => ((index + 1) / tierCount) * 100;

  // Fill % on the evenly spaced track, interpolated between neighbouring tiers.
  const fillPercent = (subtotal, tiers) => {
    let previousPosition = 0;
    let previousDollars = 0;
    for (let i = 0; i < tiers.length; i++) {
      const position = tierPosition(i, tiers.length);
      const { dollars } = tiers[i];
      if (subtotal < dollars) {
        const segmentProgress = (subtotal - previousDollars) / (dollars - previousDollars);
        return previousPosition + Math.max(0, segmentProgress) * (position - previousPosition);
      }
      previousPosition = position;
      previousDollars = dollars;
    }
    return 100;
  };

  const statusText = (subtotal, tiers) => {
    const nextTierIndex = tiers.findIndex((tier) => subtotal < tier.dollars);
    if (nextTierIndex === -1) {
      const totalBonus = tiers.reduce((sum, tier) => sum + tier.entries, 0);
      return `You've unlocked all ${withCommas(totalBonus)} bonus entries!`;
    }
    const nextTier = tiers[nextTierIndex];
    const more = nextTierIndex > 0 ? ' more' : '';
    return `Spend ${formatDollars(nextTier.dollars - subtotal)} more to earn ${withCommas(nextTier.entries)}${more} bonus entries`;
  };

  // Tier data is numeric (sanitized in cart-entries.js), so string-built markup is safe.
  const buildContentHTML = (tiers) => {
    const showTrack = tiers.length <= MAX_TRACK_TIERS;
    const labels = tiers
      .map((tier, i) => {
        const endClass = i === tiers.length - 1 ? ' bonus-bar__label--end' : '';
        return `<span class="bonus-bar__label${endClass}" data-index="${i}" style="left:${tierPosition(i, tiers.length)}%">${withCommas(tier.entries)}</span>`;
      })
      .join('');
    const markers = tiers
      .map(
        (tier, i) =>
          `<div class="bonus-bar__marker bonus-bar__marker--unreached" data-index="${i}" style="left:${tierPosition(i, tiers.length)}%">` +
          `<span class="bonus-bar__marker-text">${formatDollars(tier.dollars)}</span></div>`
      )
      .join('');

    const track = showTrack
      ? `<div class="bonus-bar__track-wrap">${labels}<div class="bonus-bar__track"><div class="bonus-bar__fill"></div>${markers}</div></div>`
      : '';
    return `${track}<p class="bonus-bar__status" aria-live="polite"></p>`;
  };

  // Tier set identity, so a sweepstake/tier change rebuilds the markup.
  const tiersSignature = (tiers) => tiers.map((tier) => `${tier.dollars}:${tier.entries}`).join('|');

  /* ── Fetch tiers + cart ────────────────────────────────────────────── */
  const fetchCart = async () => {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`cart.js ${response.status}`);
    return response.json();
  };

  const fetchState = async (requestedSweepstakeId) => {
    const tierApi = window.CS_BONUS_TIERS;
    if (!tierApi || typeof tierApi.getBonusTiers !== 'function') return null;

    const [bonus, cart] = await Promise.all([tierApi.getBonusTiers(requestedSweepstakeId), fetchCart()]);
    if (!bonus || bonus.tiers.length === 0) return null;

    // Mirrors calculateSweepstakeTotalEntries in cart-entries.js.
    const subtotalCents = bonus.calculateWithDiscount ? cart.items_subtotal_price : cart.original_total_price;
    return {
      tiers: bonus.tiers,
      subtotal: (Number(subtotalCents) || 0) / 100,
      itemCount: cart.item_count,
    };
  };

  /* ── Render / update the bar ───────────────────────────────────────── */
  const hideBar = (bar) => {
    bar.style.display = 'none';
  };

  const ensureBuilt = (bar, tiers) => {
    const content = bar.querySelector('.bonus-bar__content');
    if (!content) return null;
    const signature = tiersSignature(tiers);
    if (content.dataset.tiers !== signature || !content.querySelector('.bonus-bar__status')) {
      content.innerHTML = buildContentHTML(tiers);
      content.dataset.tiers = signature;
    }
    return content;
  };

  const render = (state) => {
    const bar = document.getElementById(BAR_ID);
    if (!bar) return;

    lastRender = state;
    if (!state || !state.itemCount || state.subtotal <= 0) {
      hideBar(bar);
      return;
    }

    const { tiers, subtotal } = state;
    const content = ensureBuilt(bar, tiers);
    if (!content) return;
    watchForMorph(bar);

    bar.classList.toggle('bonus-bar--compact', tiers.length >= COMPACT_TIER_COUNT);
    bar.style.display = '';

    const fill = content.querySelector('.bonus-bar__fill');
    if (fill) fill.style.width = `${fillPercent(subtotal, tiers)}%`;

    tiers.forEach((tier, i) => {
      const reached = subtotal >= tier.dollars;
      const marker = content.querySelector(`.bonus-bar__marker[data-index="${i}"]`);
      const label = content.querySelector(`.bonus-bar__label[data-index="${i}"]`);
      if (marker) {
        marker.classList.toggle('bonus-bar__marker--reached', reached);
        marker.classList.toggle('bonus-bar__marker--unreached', !reached);
      }
      if (label) label.classList.toggle('bonus-bar__label--reached', reached);
    });

    const status = content.querySelector('.bonus-bar__status');
    if (status) status.textContent = statusText(subtotal, tiers);
  };

  const update = async () => {
    const bar = document.getElementById(BAR_ID);
    if (!bar) return;

    // Drop responses that land after a newer request started.
    const sequence = ++requestSequence;
    try {
      const state = await fetchState(bar.dataset.sweepstakeId);
      if (sequence === requestSequence) render(state);
    } catch (error) {
      // Silently fail — bar keeps last known state
    }
  };

  /* ── Debounced refresh ─────────────────────────────────────────────── */
  const refresh = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(update, DEBOUNCE_MS);
  };

  /* ── Protect against morph clearing content ────────────────────────── */
  /**
   * Watch the .bonus-bar__content div for childList changes.
   * When morphSection() replaces the cart-items-component HTML,
   * it empties our dynamically-built content. This observer
   * fires and rebuilds from the last render before the
   * browser paints, so the bar never visually disappears.
   */
  function watchForMorph(bar) {
    if (contentObserver) contentObserver.disconnect();

    const content = bar.querySelector('.bonus-bar__content');
    if (!content) return;

    contentObserver = new MutationObserver(() => {
      if (!content.querySelector('.bonus-bar__status') && lastRender) {
        delete content.dataset.tiers;
        render(lastRender);
      }
    });

    contentObserver.observe(content, { childList: true });
  }

  /* ── Initialize ────────────────────────────────────────────────────── */
  const init = () => {
    if (document.getElementById(BAR_ID)) update();

    // Only attach listeners once (script persists across section refreshes)
    if (listenersAttached) return;
    listenersAttached = true;

    // Theme dispatches 'cart:update' (no d), our code dispatches 'cart:updated'
    document.addEventListener('cart:update', refresh);
    document.addEventListener('cart:updated', refresh);
    document.addEventListener('cart:item-added', refresh);
    // cart-entries.js finished a render: calculateWithDiscount may now be known
    document.addEventListener('cs:cart-entries-updated', refresh);

    // Re-fetch when drawer dialog opens
    const dialog = document.querySelector('cart-drawer-component dialog');
    if (dialog) {
      new MutationObserver((mutations) => {
        if (mutations.some((mutation) => mutation.attributeName === 'open') && dialog.hasAttribute('open')) {
          refresh();
        }
      }).observe(dialog, { attributes: true });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
